import type { SnapshotFrom } from 'xstate';
import { and, assign, enqueueActions, fromCallback, fromPromise, sendTo, setup } from 'xstate';
import { backoffDelayMs } from './backoff.js';
import { openRealtimeConnection, type SocketFactory } from './client.js';
import type { ClientFrame, EventNotification, Topic } from './frames.js';
import type { SequenceOutcome, SequenceTracker } from './sequences.js';

/**
 * What the workspace is showing about its connection.
 *
 * `degraded` covers both halves of being disconnected — the socket is gone, and the wait before
 * asking for another — because they are the same thing to the person looking at the screen, and a
 * banner that flickered between "lost" and "reconnecting" would say less than one that stays up.
 */
export type ConnectionStatus = 'idle' | 'connecting' | 'resyncing' | 'live' | 'degraded';

/** The gateway's frames, already told apart, plus the four things the session asks of the socket. */
export type ConnectionEvent =
  | { readonly type: 'START' }
  | { readonly type: 'STOP' }
  | { readonly type: 'TOPICS'; readonly topics: readonly Topic[] }
  /** The access token was rotated. The gateway re-verifies every `auth` frame, refresh included. */
  | { readonly type: 'TOKEN_REFRESHED' }
  /**
   * Somebody is typing in a chat pane, or has stopped.
   *
   * It goes straight out of the socket and is remembered nowhere: a typing indicator that outlived
   * the moment it described would be a lie, which is also why it is a frame and not a request. A
   * pane that types while the socket is down loses the frame, and that is the right loss.
   */
  | { readonly type: 'TYPING'; readonly topic: Topic; readonly typing: boolean }
  | { readonly type: 'gateway.closed' }
  | { readonly type: 'gateway.authenticated'; readonly expiresAt: string }
  | { readonly type: 'gateway.subscribed'; readonly topics: readonly Topic[] }
  | {
      readonly type: 'gateway.event';
      readonly notification: EventNotification;
      readonly coalesced: boolean;
    }
  | {
      readonly type: 'gateway.resynced';
      readonly aggregates: readonly { aggregateId: string; throughSequence: number }[];
    }
  | {
      readonly type: 'gateway.resyncRequired';
      readonly aggregates: readonly string[];
      readonly reason: 'beyond-retention' | 'too-far-behind';
    }
  | {
      readonly type: 'gateway.error';
      readonly code: string;
      readonly message: string;
      readonly details?: unknown;
    };

/** Everything the machine needs from outside itself, handed over once when the actor is made. */
export interface ConnectionInput {
  readonly url: string;
  readonly token: () => string | undefined;
  readonly deviceReady: () => boolean;
  /** The topics the session wants. `TOPICS` replaces them when the permissions change. */
  readonly topics: readonly Topic[];
  /**
   * What the client has applied, per aggregate. Handed in rather than created here because it
   * outlives any one socket: it is what `subscribe` and `resync` are built from after a drop.
   */
  readonly sequences: SequenceTracker;
  /** Applies one notification: invalidate or patch the caches it touches, feed the session. */
  readonly applyEvent: (notification: EventNotification, outcome: SequenceOutcome) => void;
  /**
   * The other side of a chat started or stopped typing.
   *
   * A callback rather than machine state, for the same reason `applyEvent` is one: it is not part
   * of the connection's own story, and holding it in the chart would mean the chart re-rendering
   * every screen on every keystroke of every visitor.
   */
  readonly onPeerTyping?: (topic: string, from: 'visitor' | 'agent', typing: boolean) => void;
  /**
   * Reads the server's state back when a replay could not cover the gap, and does not resolve until
   * the workspace is showing it. The chart stays out of `live` until it does, which is the whole of
   * FR-WS-3: an agent must never act on stale call state.
   */
  readonly reconcileSnapshots: () => Promise<void>;
  readonly socket?: SocketFactory;
  readonly heartbeatMs?: number;
  readonly random?: () => number;
}

interface ConnectionContext extends ConnectionInput {
  topics: readonly Topic[];
  /** The topics the gateway accepted, with `agent:me` resolved to this agent's id. */
  resolvedTopics: readonly Topic[];
  agentId: string | undefined;
  attempt: number;
  /** Aggregates a `resync` asked about and that have not been answered yet. */
  awaitingResync: readonly string[];
  /** True once something has to be read back through `/v1` because no replay can cover it. */
  needsSnapshots: boolean;
  lastError: { readonly code: string; readonly message: string } | undefined;
}

/** The agent topic the gateway resolved, which is where the client learns its own agent id. */
function agentIdFrom(topics: readonly Topic[]): string | undefined {
  const resolved = topics.find((topic) => topic.startsWith('agent:') && topic !== 'agent:me');
  return resolved?.slice('agent:'.length);
}

/**
 * The aggregates a replay could actually cover.
 *
 * The gateway serves a `resync` only for an aggregate the socket is **subscribed** to, and answers
 * `RESYNC_FORBIDDEN` for the rest — so a client that asked for everything it had ever seen would be
 * waiting for an answer about an aggregate the gateway will never answer about, and would sit there
 * showing `resyncing` until something dropped the socket.
 *
 * `tenant:config` is the one that makes this real rather than theoretical: it is the busiest topic,
 * its events are `Configuration` aggregates whose ids land in the tracker like any other, and the
 * topic carries no aggregate id for the gateway to match them against. Those are read back from
 * `/v1` instead, which is what the client would do with a configuration change anyway.
 */
function replayableIds(context: {
  readonly resolvedTopics: readonly Topic[];
  readonly sequences: SequenceTracker;
}): Set<string> {
  const subscribed = new Set(
    context.resolvedTopics
      .map((topic) => topic.slice(topic.indexOf(':') + 1))
      .filter((id) => id !== 'config'),
  );
  return new Set(
    context.sequences
      .resyncRequests()
      .map((request) => request.aggregateId)
      .filter((aggregateId) => subscribed.has(aggregateId)),
  );
}

/** The aggregate ids an `error` frame's details name, if it names any. */
function aggregatesNamedIn(details: unknown): readonly string[] {
  if (typeof details !== 'object' || details === null) {
    return [];
  }
  const named = (details as { aggregates?: unknown }).aggregates;
  return Array.isArray(named) ? named.filter((id): id is string => typeof id === 'string') : [];
}

/**
 * The connection, as a statechart (ADR-0006).
 *
 * A statechart rather than a handful of booleans because the question it answers is a sequence and
 * not a flag: authenticate, subscribe, catch up, and only then stop warning the agent that their
 * screen may be wrong. Written as booleans, "connected" and "caught up" drift apart, and the bug
 * that follows is an agent answering a call the server already gave to somebody else.
 *
 * The socket is invoked by the `session` state, so the chart owns its lifetime: leaving `session`
 * for `degraded` closes it, entering `session` opens a new one, and there is no path where a
 * machine showing `degraded` still has a socket delivering events.
 */
export const connectionMachine = setup({
  types: {} as {
    context: ConnectionContext;
    events: ConnectionEvent;
    input: ConnectionInput;
  },
  actors: {
    /**
     * The socket, turned into machine events.
     *
     * The frames are told apart here rather than inside the chart, so the states deal in facts —
     * `authenticated`, `subscribed`, `resynced` — instead of one `frame` event that every state
     * would have to re-inspect.
     */
    socket: fromCallback<
      ClientFrame,
      {
        url: string;
        token: () => string | undefined;
        deviceReady: () => boolean;
        socket: SocketFactory | undefined;
        heartbeatMs: number | undefined;
        onPeerTyping:
          | ((topic: string, from: 'visitor' | 'agent', typing: boolean) => void)
          | undefined;
      }
    >(({ sendBack, receive, input }) => {
      const connection = openRealtimeConnection({
        url: input.url,
        token: input.token,
        deviceReady: input.deviceReady,
        socket: input.socket,
        heartbeatMs: input.heartbeatMs,
        onFrame(frame) {
          switch (frame.type) {
            case 'authenticated':
              sendBack({ type: 'gateway.authenticated', expiresAt: frame.expiresAt });
              return;
            case 'subscribed':
              sendBack({ type: 'gateway.subscribed', topics: frame.topics });
              return;
            case 'event':
              sendBack({
                type: 'gateway.event',
                notification: frame.event,
                coalesced: frame.coalesced === true,
              });
              return;
            case 'resynced':
              sendBack({ type: 'gateway.resynced', aggregates: frame.aggregates });
              return;
            case 'resync-required':
              sendBack({
                type: 'gateway.resyncRequired',
                aggregates: frame.aggregates,
                reason: frame.reason,
              });
              return;
            case 'error':
              sendBack({
                type: 'gateway.error',
                code: frame.code,
                message: frame.message,
                details: frame.details,
              });
              return;
            case 'peer-typing':
              // Straight past the chart: this is the one frame that is neither an event nor part
              // of the connection's own story, and a state transition per keystroke would be a
              // statechart doing an animation's job.
              input.onPeerTyping?.(frame.topic, frame.from, frame.typing);
              return;
            case 'heartbeat':
              // The ack. The round trip is the point of it, and "the socket is open" is something
              // the chart already knows.
              return;
          }
        },
        onClosed() {
          sendBack({ type: 'gateway.closed' });
        },
      });

      receive((frame) => connection.send(frame));
      return () => connection.close();
    }),

    /** Reading the server back, which is what a client no replay can cover does instead. */
    snapshots: fromPromise<void, { reconcile: () => Promise<void> }>(async ({ input }) =>
      input.reconcile(),
    ),
  },
  actions: {
    applyNotification: ({ context, event }) => {
      if (event.type !== 'gateway.event') {
        return;
      }
      // A gap repairs itself here rather than in the chart: `applyEvent` invalidates the caches the
      // aggregate owns, and an invalidated query is a snapshot refetch. What a gap must not do is
      // send the whole session back through reconciliation — the events in between are missing from
      // one aggregate's stream, not from the client's idea of who it is.
      const outcome = context.sequences.accept(event.notification, event.coalesced);
      context.applyEvent(event.notification, outcome);
    },
    /**
     * What the session wants watched, remembered wherever the chart happens to be.
     *
     * **At the root, because `idle` used to drop it.** The session provider sends `TOPICS` and then
     * `START` in the same effect, and an event no state handles is gone — so the chart entered
     * `session` with no topics, authenticated, and subscribed to nothing. The gateway refused the
     * frame (`FRAME_INVALID`) and closed the socket, the chart reconnected, and the workspace sat
     * on "the live connection has been lost" for ever. Whether it recovered depended on a React
     * re-render delivering `TOPICS` again before the socket authenticated, which the development
     * server won (hundreds of unbundled modules) and a built bundle lost — which is why this only
     * ever appeared behind a real deployment's one origin.
     */
    rememberTopics: assign({
      topics: ({ context, event }) => (event.type === 'TOPICS' ? event.topics : context.topics),
    }),
    subscribe: sendTo('socket', ({ context }) => ({
      type: 'subscribe' as const,
      topics: context.topics,
      lastSequences: context.sequences.lastSequences(),
    })),
    sendToken: sendTo('socket', ({ context }) => ({
      type: 'auth' as const,
      token: context.token() ?? '',
    })),
    relayTyping: enqueueActions(({ event, enqueue }) => {
      if (event.type !== 'TYPING') {
        return;
      }
      enqueue.sendTo('socket', {
        type: 'typing' as const,
        topic: event.topic,
        typing: event.typing,
      });
    }),
    rememberSubscriptions: assign({
      resolvedTopics: ({ event }) => (event.type === 'gateway.subscribed' ? event.topics : []),
      agentId: ({ event }) =>
        event.type === 'gateway.subscribed' ? agentIdFrom(event.topics) : undefined,
      // The gateway accepted this socket's subscriptions, which is the first point at which it has
      // actually worked. Resetting on `open` instead would retry a gateway that accepts a socket
      // and then refuses it twice a second for ever.
      attempt: 0,
    }),
    rememberError: assign({
      lastError: ({ event }) =>
        event.type === 'gateway.error' ? { code: event.code, message: event.message } : undefined,
    }),
    rememberRefetchFailure: assign({
      lastError: () => ({
        code: 'SNAPSHOT_REFETCH_FAILED',
        message: 'the workspace could not read the server’s state; it will try again',
      }),
    }),
    /**
     * Splits what the client is behind on into what can be replayed and what cannot, and forgets
     * its place in the second group — a snapshot answers those, and a position the client keeps
     * claiming would make the first event after it look like a duplicate or a gap.
     */
    planTheCatchUp: assign(({ context }) => {
      const replayable = replayableIds(context);
      const unreplayable = context.sequences
        .resyncRequests()
        .map((request) => request.aggregateId)
        .filter((aggregateId) => !replayable.has(aggregateId));
      for (const aggregateId of unreplayable) {
        context.sequences.forget(aggregateId);
      }
      return { awaitingResync: [...replayable], needsSnapshots: unreplayable.length > 0 };
    }),
    askForReplay: enqueueActions(({ context, enqueue }) => {
      const aggregates = context.sequences.resyncRequests();
      // A `resync` naming nothing is a frame the gateway's schema refuses, and a refused frame
      // closes the socket. Everything the client is behind on is being read back instead.
      if (aggregates.length === 0) {
        return;
      }
      enqueue.sendTo('socket', { type: 'resync' as const, aggregates });
    }),
    settleReplayed: assign(({ context, event }) => {
      if (event.type !== 'gateway.resynced') {
        return {};
      }
      for (const entry of event.aggregates) {
        context.sequences.seed(entry.aggregateId, entry.throughSequence);
      }
      const settled = new Set(event.aggregates.map((entry) => entry.aggregateId));
      return { awaitingResync: context.awaitingResync.filter((id) => !settled.has(id)) };
    }),
    settleUnreplayable: assign(({ context, event }) => {
      if (event.type !== 'gateway.resyncRequired') {
        return {};
      }
      // The client's place in these streams is lost, so it must stop claiming one: what follows the
      // snapshot is whatever the server sends next, not "one after the sequence I used to have".
      for (const aggregateId of event.aggregates) {
        context.sequences.forget(aggregateId);
      }
      const unreplayable = new Set(event.aggregates);
      return {
        awaitingResync: context.awaitingResync.filter((id) => !unreplayable.has(id)),
        needsSnapshots: true,
      };
    }),
    /**
     * The gateway will not replay something the client asked about.
     *
     * It should not happen — the client only asks about aggregates the gateway's own `subscribed`
     * frame named — but if the two ever disagree, the aggregate would never be answered for and the
     * workspace would sit showing `resyncing` until something dropped the socket. So a refusal is
     * an answer: stop waiting for those, and read them back instead.
     */
    stopWaitingForRefused: assign(({ context, event }) => {
      if (event.type !== 'gateway.error' || event.code !== 'RESYNC_FORBIDDEN') {
        return {};
      }
      const refused = new Set(aggregatesNamedIn(event.details));
      return {
        // A refusal that names nothing stops the client waiting for all of it: there is no way to
        // tell which aggregate it was about, and waiting for ever is the worse answer.
        awaitingResync:
          refused.size === 0 ? [] : context.awaitingResync.filter((id) => !refused.has(id)),
        needsSnapshots: true,
      };
    }),
    clearSnapshotsNeeded: assign({ needsSnapshots: false }),
    countAttempt: assign({ attempt: ({ context }) => context.attempt + 1 }),
  },
  delays: {
    reconnect: ({ context }) => backoffDelayMs(context.attempt, context.random ?? Math.random),
  },
  guards: {
    /** Nothing applied yet means nothing to catch up on: a first connection is already level. */
    hasSomethingToResync: ({ context }) => !context.sequences.isEmpty,
    /**
     * Something to ask for.
     *
     * The gateway's `subscribe` requires at least one topic and closes a socket that sends an
     * invalid frame, so a client that subscribes before it knows what to watch reconnects for
     * ever. Waiting is the only correct thing to do with an empty list.
     */
    hasTopics: ({ context }) => context.topics.length > 0,
    eventHasTopics: ({ event }) => event.type === 'TOPICS' && event.topics.length > 0,
    everyAnswerIn: ({ context }) => context.awaitingResync.length === 0,
    anythingToReadBack: ({ context }) => context.needsSnapshots,
  },
}).createMachine({
  id: 'connection',
  context: ({ input }) => ({
    ...input,
    resolvedTopics: [],
    agentId: undefined,
    attempt: 0,
    awaitingResync: [],
    needsSnapshots: false,
    lastError: undefined,
  }),
  initial: 'idle',
  // Remembered in every state. The states that *act* on it — `connecting`, which has not subscribed
  // yet, and `live`, which has — handle it themselves and shadow this.
  on: { TOPICS: { actions: 'rememberTopics' } },
  states: {
    /** No session, so no socket: the workspace says nothing about a connection it is not making. */
    idle: {
      id: 'idle',
      on: { START: 'session' },
    },

    /** One socket's lifetime. Its children are the three things that happen over one socket. */
    session: {
      invoke: {
        id: 'socket',
        src: 'socket',
        input: ({ context }) => ({
          url: context.url,
          token: context.token,
          deviceReady: context.deviceReady,
          socket: context.socket,
          heartbeatMs: context.heartbeatMs,
          onPeerTyping: context.onPeerTyping,
        }),
      },
      on: {
        'gateway.closed': '#degraded',
        'gateway.error': { actions: 'rememberError' },
        TOKEN_REFRESHED: { actions: 'sendToken' },
        // Only while there is a socket. Outside `session` the event is ignored rather than
        // queued: a keystroke is not worth delivering once the moment it described has passed.
        TYPING: { actions: 'relayTyping' },
        STOP: '#idle',
      },
      initial: 'connecting',
      states: {
        connecting: {
          on: {
            'gateway.subscribed': [
              {
                guard: 'hasSomethingToResync',
                target: 'resyncing',
                actions: 'rememberSubscriptions',
              },
              { target: 'live', actions: 'rememberSubscriptions' },
            ],
          },
          initial: 'authenticating',
          states: {
            authenticating: {
              on: {
                // Subscribing carries what the client already has, per aggregate, so a reconnection
                // does not replay what it applied before the socket went.
                'gateway.authenticated': [
                  { guard: 'hasTopics', target: 'subscribing', actions: 'subscribe' },
                  { target: 'awaitingTopics' },
                ],
              },
            },

            /**
             * Authenticated, with nothing to ask for yet — the socket opened before the session
             * said what this agent watches. It subscribes the moment it is told, and sends nothing
             * in the meantime, because an empty `subscribe` is the frame that cost this chart a
             * silent reconnection loop.
             */
            awaitingTopics: {
              on: {
                TOPICS: {
                  guard: 'eventHasTopics',
                  target: 'subscribing',
                  actions: ['rememberTopics', 'subscribe'],
                },
              },
            },

            /**
             * Asked, waiting for the gateway to say what it resolved.
             *
             * A `TOPICS` arriving in this window — a screen pop following a new interaction while a
             * reconnection is still subscribing — asks again rather than waiting for the next change
             * that may never come. The protocol allows it: topics are authorised one at a time and a
             * resubscribe carrying `lastSequences` replays nothing.
             */
            subscribing: {
              on: {
                TOPICS: { guard: 'eventHasTopics', actions: ['rememberTopics', 'subscribe'] },
              },
            },
          },
        },

        /**
         * Catching up. The workspace still shows itself as degraded here: the socket is back, but
         * what is on the screen is what was there before it went.
         */
        resyncing: {
          initial: 'replaying',
          states: {
            replaying: {
              entry: ['planTheCatchUp', 'askForReplay'],
              always: [
                { guard: and(['everyAnswerIn', 'anythingToReadBack']), target: 'reading' },
                { guard: 'everyAnswerIn', target: 'done' },
                // Anything still unanswered keeps the workspace degraded, which is the honest
                // state until the gateway has said one way or the other about every aggregate.
              ],
              on: {
                'gateway.event': { actions: 'applyNotification' },
                'gateway.resynced': { actions: 'settleReplayed' },
                'gateway.resyncRequired': { actions: 'settleUnreplayable' },
                'gateway.error': { actions: ['rememberError', 'stopWaitingForRefused'] },
              },
            },

            /** Reading the server back, for everything no replay could cover. */
            reading: {
              invoke: {
                src: 'snapshots',
                input: ({ context }) => ({ reconcile: context.reconcileSnapshots }),
                onDone: { target: 'done', actions: 'clearSnapshotsNeeded' },
                // A snapshot the workspace could not read leaves it degraded, on purpose: showing
                // `live` over state nobody confirmed is the one failure this chart exists to
                // prevent, and the backoff brings it round again.
                onError: { target: '#degraded', actions: 'rememberRefetchFailure' },
              },
              on: { 'gateway.event': { actions: 'applyNotification' } },
            },

            done: { type: 'final' },
          },
          onDone: 'live',
        },

        live: {
          on: {
            'gateway.event': { actions: 'applyNotification' },
            // A second `subscribed` — the answer to a resubscribe sent while the first was in
            // flight — is what the gateway resolved, so it replaces what was recorded.
            'gateway.subscribed': { actions: 'rememberSubscriptions' },
            TOPICS: { actions: ['rememberTopics', 'subscribe'] },
          },
        },
      },
    },

    /**
     * No socket, and waiting before asking for another. The agent sees the banner the whole time.
     */
    degraded: {
      id: 'degraded',
      entry: 'countAttempt',
      after: { reconnect: 'session' },
      on: { STOP: '#idle' },
    },
  },
});

export type ConnectionSnapshot = SnapshotFrom<typeof connectionMachine>;

/**
 * The one word the workspace shows, from wherever the chart is.
 *
 * Derived rather than stored, so there is no second copy of the connection's state to go stale —
 * and taken from the leaf, because `session.resyncing.reading` and `session.resyncing.replaying`
 * are the same thing to the person waiting: still catching up.
 */
export function statusOf(snapshot: ConnectionSnapshot): ConnectionStatus {
  if (snapshot.matches('idle')) {
    return 'idle';
  }
  if (snapshot.matches('degraded')) {
    return 'degraded';
  }
  if (snapshot.matches({ session: 'live' })) {
    return 'live';
  }
  if (snapshot.matches({ session: 'connecting' })) {
    return 'connecting';
  }
  return 'resyncing';
}
